<template>
  <div class="product-evaluation-copy critical-component-clear">
    <div
      class="product-evaluation-copy__wrapper"
      :class="htmlContentType"
    >
      <div
        class="product-evaluation-copy__tick-icon-wrapper"
        :class="htmlContentType"
      >
        <icon-check />
      </div>
      <div
        class="product-evaluation-copy__content"
        :class="htmlContentType"
      >
        <div
          class="product-evaluation-copy__title-wrapper"
          :class="htmlContentType"
        >
          <p
            class="product-evaluation-copy__icon"
            :class="htmlContentType"
          >
            <icon-alert />
          </p>
          <p
            class="product-evaluation-copy__title text-body-s-bold"
            :class="htmlContentType"
            v-text="activeContent.title"
          />
        </div>
        <p
          class="product-evaluation-copy__body text-body-s-regular"
          :class="htmlContentType"
          v-text="activeContent.description"
        />
      </div>
      <btn
        v-if="htmlContentType === 'initial'"
        class="product-evaluation-copy__button"
        :class="htmlContentType"
        modifiers="secondary block center"
        label=""
        show-icon
        type="button"
        @click="showConfirmationBox"
      >
        <icon-arrow-right />
      </btn>
      <btn
        v-if="htmlContentType === 'confirmation'"
        class="product-evaluation-copy__button"
        :class="htmlContentType"
        :label="ctaText"
        modifiers="primary"
        @click="handleAddToCart"
      />
      <btn
        v-if="htmlContentType === 'blocked'"
        class="product-evaluation-copy__button"
        :class="htmlContentType"
        :label="ctaText"
        modifiers="primary"
        url="/cart"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product evaluation copy (product-evaluation-copy)
 * -----------------------------------------------------------------------------
 * Product evaluation copy section.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapActions, mapGetters } from 'vuex'
import Btn from '~global/btn/btn'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconAlert from '~icons/general/alert.svg'
import IconCheck from '~icons/general/check.svg'

export default {
  name: 'ProductEvaluationCopy',

  components: {
    Btn,
    IconArrowRight,
    IconAlert,
    IconCheck,
  },

  props: {
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
      }),
    },
    cartItems: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      htmlContentType: '',
      ctaText: '',
      activeContent: false,
      isInitialClicked: false,
      isEvalAdded: false,
      regularProductsInCart: false,
      cartProducts: [],
      lineItemProperties: {
        [this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')]: true,
      },
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getInitialData: 'product-eval-copy/getInitialData',
      getConfirmationData: 'product-eval-copy/getConfirmationData',
      getSuccessData: 'product-eval-copy/getSuccessData',
      getBlockedData: 'product-eval-copy/getBlockedData',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
  },

  watch: {
    cartItems() {
      this.init()
    },
  },

  mounted() {
    this.init()
  },

  methods: {

    init() {
      this.checkEvalProductInBasket()
      this.checkRegularProductInCart()
      this.buildSectionHtml()
    },

    /**
     * Checks if there is evaluation copy product in basket
     */
    checkEvalProductInBasket() {
      this.checkProductInBasket = this.cartItems.some((item) => {
        return item.id === this.variant.id && item.properties?.[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')]
      })
    },

    /**
     * Checks if there is regular product in basket
     */
    checkRegularProductInCart() {
      this.cartContainsRegularProduct = this.cartItems.some((item) => {
        return !item.properties?.[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')]
      })
    },

    /**
     * Shows section according to the cart content
     */
    buildSectionHtml() {
      if (this.checkProductInBasket) {
        this.showSuccessMessage()

        return
      }

      if (!this.cartContainsRegularProduct) {
        this.showInitialMessage()

        return
      }

      this.showBlockedMessage()
    },

    /**
     * Shows initial message - when there are no regular products in cart
     */
    showInitialMessage() {
      this.htmlContentType = 'initial'
      this.activeContent = this.getInitialData
    },

    /**
     * Shows confirmation message - once user clicks evaluation copy arrow
     */
    showConfirmationBox() {
      this.htmlContentType = 'confirmation'
      this.activeContent = this.getConfirmationData
      this.ctaText = this.$string('product.cta.add_to_cart')
    },

    /**
     * Shows success message - once product is added as evaluation copy
     */
    showSuccessMessage() {
      this.htmlContentType = 'success'
      this.activeContent = this.getSuccessData
    },

    /**
     * Shows blocked message
     */
    showBlockedMessage() {
      this.htmlContentType = 'blocked'
      this.activeContent = this.getBlockedData
      this.ctaText = this.$string('product.go_to_basket')
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Add evaluation copy product
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.$string('product.form.select_variant')
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      this.lineItemProperties[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')] = true
      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        this.showSuccessMessage()
        // await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add evaluation copy product to cart', error)
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-evaluation-copy';
</style>
